import { EventInfo, Recording } from '@app/routes/content/domain';
import { useAppDispatch, useAppSelector } from '@app/store';
import { getBaseUrl } from '@hopin-team/dashboard-constants';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback } from 'react';

import { selectRecording, selectRecordingEventInfo } from '../../../selector';
import { TearOutWindow } from '../../icons';
import { Switch } from '../../switch';
import { Description } from './description';
import { SectionCard } from './section-card';

const getEventStatus = (eventInfo: EventInfo | null) => {
  const { timeStart = '', timeEnd = '', status, expired } = eventInfo || {};
  const parsedTimeStart = new Date(timeStart);
  const parsedTimeEnd = new Date(timeEnd);
  const currentDate = new Date();

  if (expired) {
    return 'expired';
  } else if (status === 'DRAFT') {
    return 'draft';
  } else if (status === 'BUILDING') {
    return 'building';
  } else {
    if (parsedTimeEnd <= currentDate) {
      return 'ended';
    } else if (parsedTimeStart >= currentDate) {
      return 'upcoming';
    }
    return 'live';
  }
};

export const EventDetails = () => {
  const { t } = useTranslation();
  const eventInfo = useAppSelector(selectRecordingEventInfo);
  const { name, imageUrl, slug } = eventInfo || {};
  const recording = useAppSelector(selectRecording) as Recording;
  const { id: recordingId, published } = recording || {};
  const dispatch = useAppDispatch();
  const hopinBaseUrl = getBaseUrl();
  const eventOverviewUrl = `${hopinBaseUrl}/organisers/events/${slug}/overview`;
  const eventStatus = getEventStatus(eventInfo);

  const onPublishClick = useCallback(() => {
    if (!recordingId || !recording) return;

    dispatch({
      type: '[recording] toggle publish started',
      payload: {
        recordingId,
        published: !published,
      },
    });
  }, [recordingId, recording, dispatch, published]);

  return (
    <SectionCard className="!p-0 gap-2">
      <div className="flex items-center mb-3 px-2 pt-2">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-[71px] h-[74px] bg-black/20 rounded-lg flex items-center justify-center flex-shrink-0">
            {imageUrl && (
              <img
                src={imageUrl}
                alt=""
                className="w-full h-full rounded-lg object-cover"
              />
            )}
          </div>
          <div className="flex-1 min-w-0 my-0.5">
            <div className="flex flex-col items-start gap-1">
              <span className="text-spring-neutral-b0/50 text-xs font-medium">
                {t('content-recording.content-menu.from-event')}
              </span>
              <p className="text-white text-sm font-semibold leading-6 flex-1 min-h-0 items-center p-0 m-0">
                {name}
              </p>
              <span className="bg-spring-neutral-b5 text-spring-neutral-b1 text-xs font-semibold px-1 py-0.5 rounded h-4 flex items-center">
                {t(
                  `content-recording.content-menu.event-status.${eventStatus}`,
                )}
              </span>
            </div>
          </div>
          <a
            href={eventOverviewUrl}
            className="flex items-center justify-center !border !border-solid !border-white/20 rounded-xl hover:bg-gray-800 transition-colors flex-shrink-0 w-9 h-9 text-white"
            rel="noreferrer"
            target="_blank"
          >
            <TearOutWindow />
          </a>
        </div>
      </div>
      {recording?.category === 'main' && (
        <div className="flex items-center gap-2 p-2">
          <Switch
            checked={!!published}
            onClick={onPublishClick}
            data-testid="event-details-publish-switch"
          />
          <span className="text-white/50 text-xs font-medium leading-4 max-w-[300px] break-words">
            {recording?.published
              ? t('content-recording.auto-deactivate-replay')
              : t('content-recording.auto-activate-replay')}
          </span>
        </div>
      )}
      <Description />
    </SectionCard>
  );
};
